import { useQuery } from '@tanstack/react-query';
import { History, ArrowRight, Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';

type FieldChange = { field: string; label: string; before: any; after: any };

type EditLog = {
  id: string;
  warehouseId: number;
  merchantId?: number | null;
  userId?: number | null;
  actorName?: string | null;
  actorEmpCode?: string | null;
  actorType: string;
  action: string;
  changes: FieldChange[];
  ip?: string | null;
  createdAt: string;
};

/** Render a stored value for reading. Booleans are the Status flag; empty means it had none. */
function show(v: any): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Active' : 'Inactive';
  return String(v);
}

const ACTION_STYLE: Record<string, string> = {
  CREATE: 'bg-emerald-50 text-emerald-700',
  UPDATE: 'bg-indigo-50 text-indigo-700',
  DELETE: 'bg-rose-50 text-rose-700',
};

/**
 * Who changed this pickup location, when, and what it was before.
 *
 * **Internal staff only — never render this for a merchant identity.** It carries our own people's
 * names and emp codes. The API refuses merchant callers on the same route, so this is the second of
 * two guards rather than the only one.
 */
export function WarehouseEditHistory({
  merchantId,
  warehouseId,
  warehouseName,
  onClose,
}: {
  merchantId: number;
  warehouseId: number;
  warehouseName?: string;
  onClose: () => void;
}) {
  const { data: logs = [], isLoading, error } = useQuery({
    queryKey: ['warehouse-edit-logs', merchantId, warehouseId],
    queryFn: async () =>
      (await api.get<EditLog[]>(`/merchants/${merchantId}/warehouses/${warehouseId}/edit-logs`)).data,
  });

  const rows = logs as EditLog[];

  return (
    <Modal isOpen onClose={onClose} title="Change history"
      subtitle={`${warehouseName ?? `Pickup location #${warehouseId}`} — internal only, not visible to merchants`}
      maxWidth="2xl">
      {isLoading ? (
        <div className="py-10 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin" /> Loading…</div>
      ) : error ? (
        <div className="py-10 text-center text-sm text-slate-500">Could not load the history for this pickup location.</div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-500">
          This pickup location has not been changed since change history was switched on.
        </div>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {rows.map((l) => (
            <div key={l.id} className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${ACTION_STYLE[l.action] ?? 'bg-slate-100 text-slate-600'}`}>
                  {l.action}
                </span>
                <span className="text-sm font-medium text-slate-900">{l.actorName ?? 'Unknown'}</span>
                {l.actorEmpCode && <span className="text-xs text-slate-400 font-mono">{l.actorEmpCode}</span>}
                {l.actorType === 'merchant' && (
                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">merchant</span>
                )}
                <span className="ml-auto text-xs text-slate-400">
                  {new Date(l.createdAt).toLocaleString('en-IN')}
                  {l.ip && <span className="ml-2 font-mono">{l.ip}</span>}
                </span>
              </div>
              <div className="space-y-1">
                {(l.changes ?? []).map((c, i) => (
                  <div key={i} className="text-xs flex items-baseline gap-2 flex-wrap">
                    <span className="text-slate-500 min-w-[120px]">{c.label}</span>
                    <span className="text-slate-400 line-through">{show(c.before)}</span>
                    <ArrowRight size={11} className="text-slate-300 shrink-0" />
                    <span className="text-slate-900 font-medium">{show(c.after)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** The button that opens the history. Kept here so every pickup-location screen opens it the same way. */
export function WarehouseHistoryButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} title="Change history"
      className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded">
      <History size={14} />
    </button>
  );
}
